import { useState, FormEvent } from "react";

// Número de WhatsApp no formato internacional (sem + ou espaços)
const WHATSAPP_NUMBER = "5581995913182";

const CASE_AREAS = [
  "Pensão por Morte",
  "Cumulação de Pensão",
  "Benefício LOAS",
  "Outro",
];

const CONTACT_ITEMS = [
  {
    icon: "ri-whatsapp-line",
    label: "WhatsApp",
    value: "(81) 99591-3182",
    href: "https://wa.me/5581995913182?text=Ol%C3%A1%2C%20gostaria%20de%20agendar%20uma%20consulta",
    description: "Resposta rápida, fale agora",
  },
  {
    icon: "ri-phone-line",
    label: "Telefone",
    value: "(81) 99591-3182",
    href: "tel:+5581995913182",
    description: "Seg–Sex, 8h às 18h",
  },
  {
    icon: "ri-mail-line",
    label: "E-mail",
    value: "martins.adv.suellen@gmail.com",
    href: "mailto:martins.adv.suellen@gmail.com",
    description: "Para documentos e consultas",
  },
];

export default function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    caseArea: "",
    message: "",
  });
  const [charCount, setCharCount] = useState(0);

  // Atualiza os campos do formulário
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    if (name === "message") {
      if (value.length > 500) return;
      setCharCount(value.length);
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Ao enviar: monta a mensagem e abre o WhatsApp em nova aba
  const handleSubmit = (e: FormEvent) => {
    // Impede o comportamento padrão do formulário (sem reload/fetch)
    e.preventDefault();

    // Valida que todos os campos obrigatórios estão preenchidos
    if (!formData.name || !formData.email || !formData.caseArea || !formData.message) return;
    if (charCount > 500) return;

    // Monta a mensagem com a estrutura exata solicitada
    const rawMessage =
      `Nome Completo: ${formData.name} | ` +
      `E-mail: ${formData.email} | ` +
      `Área do Caso: ${formData.caseArea} | ` +
      `Motivo do Contato: ${formData.message}`;

    // Codifica a mensagem para URL
    const encodedMessage = encodeURIComponent(rawMessage);

    // Abre o WhatsApp em nova aba com a mensagem pronta
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodedMessage}`, "_blank");
  };

  return (
    <section id="contato" className="w-full bg-white py-28 px-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <p className="font-body text-peach text-xs tracking-[0.35em] uppercase font-medium mb-4">
            Entre em contato
          </p>
          <h2 className="font-display text-4xl lg:text-5xl text-ink font-semibold leading-tight mb-4">
            Vamos{" "}
            <span className="font-light italic" style={{ color: "#7A2C1E" }}>
              conversar?
            </span>
          </h2>
          <div className="w-10 h-px bg-peach mx-auto mt-6 mb-7" />
          <p className="font-body text-ink/60 text-base max-w-xl mx-auto leading-relaxed">
            Entre em contato por qualquer canal abaixo ou preencha o formulário. Respondemos com agilidade e atenção.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Left: Contact Info */}
          <div className="flex flex-col gap-4">
            {CONTACT_ITEMS.map((item) => (
              <a
                key={item.label}
                href={item.href}
                target={item.href.startsWith("http") ? "_blank" : undefined}
                rel={item.href.startsWith("http") ? "noopener noreferrer nofollow" : undefined}
                className="group flex items-center gap-5 bg-parchment rounded-xl p-5 transition-colors duration-300 cursor-pointer"
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLAnchorElement).style.backgroundColor = "rgb(61, 35, 32)";
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLAnchorElement).style.backgroundColor = "";
                }}
              >
                <div className="w-11 h-11 flex items-center justify-center rounded-lg bg-white flex-shrink-0 group-hover:bg-white/10 transition-colors duration-300">
                  <i className={`${item.icon} text-xl transition-colors duration-300 text-chestnut group-hover:text-white`} />
                </div>
                <div>
                  <p className="font-body text-xs text-ink/40 group-hover:text-white/50 font-medium uppercase tracking-widest transition-colors duration-300">{item.label}</p>
                  <p className="font-body text-sm font-semibold text-ink group-hover:text-white mt-0.5 transition-colors duration-300">{item.value}</p>
                  <p className="font-body text-xs text-ink/40 group-hover:text-white/50 mt-0.5 transition-colors duration-300">{item.description}</p>
                </div>
                <div className="ml-auto w-5 h-5 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-ink/20 group-hover:text-white/50 transition-colors duration-300" />
                </div>
              </a>
            ))}

            {/* Map */}
            <div className="rounded-xl overflow-hidden h-44 mt-2">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d247867.5083083573!2d-35.04560585!3d-8.05428!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x7ab1836e6e43a7b%3A0x65d1e2a9e41be3f5!2sRecife%2C%20PE!5e0!3m2!1spt-BR!2sbr!4v1699999999999"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                title="Localização – Recife, PE"
              />
            </div>
          </div>

          {/* Right: Form */}
          <div className="rounded-xl p-8" style={{ backgroundColor: "rgb(61, 35, 32)" }}>
            <h3 className="font-display text-2xl font-semibold text-white mb-7">
              Envie uma mensagem
            </h3>

            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              {/* Nome Completo */}
              <div>
                <label className="font-body text-xs text-white/60 font-medium block mb-1.5 uppercase tracking-wide">
                  Nome completo <span className="text-peach">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="Seu nome completo"
                  className="w-full rounded-md px-4 py-3 font-body text-sm text-white placeholder-white/30 focus:outline-none focus:border-peach bg-white/10 border border-white/20"
                />
              </div>

              {/* E-mail */}
              <div>
                <label className="font-body text-xs text-white/60 font-medium block mb-1.5 uppercase tracking-wide">
                  E-mail <span className="text-peach">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="seu@email.com"
                  className="w-full rounded-md px-4 py-3 font-body text-sm text-white placeholder-white/30 focus:outline-none focus:border-peach bg-white/10 border border-white/20"
                />
              </div>

              {/* Área do Caso */}
              <div>
                <label className="font-body text-xs text-white/60 font-medium block mb-1.5 uppercase tracking-wide">
                  Área do Caso <span className="text-peach">*</span>
                </label>
                <select
                  name="caseArea"
                  value={formData.caseArea}
                  onChange={handleChange}
                  required
                  className="w-full rounded-md px-4 py-3 font-body text-sm text-white focus:outline-none focus:border-peach bg-white/10 border border-white/20 cursor-pointer appearance-none"
                  style={{ backgroundColor: "rgba(255,255,255,0.1)" }}
                >
                  <option value="" disabled style={{ backgroundColor: "rgb(61,35,32)", color: "rgba(255,255,255,0.4)" }}>
                    Selecione uma área
                  </option>
                  {CASE_AREAS.map((area) => (
                    <option
                      key={area}
                      value={area}
                      style={{ backgroundColor: "rgb(61,35,32)", color: "#fff" }}
                    >
                      {area}
                    </option>
                  ))}
                </select>
              </div>

              {/* Mensagem */}
              <div>
                <label className="font-body text-xs text-white/60 font-medium block mb-1.5 uppercase tracking-wide">
                  Mensagem <span className="text-peach">*</span>
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={4}
                  placeholder="Conte brevemente sobre seu caso ou dúvida..."
                  className="w-full rounded-md px-4 py-3 font-body text-sm text-white placeholder-white/30 focus:outline-none focus:border-peach bg-white/10 border border-white/20 resize-none"
                />
                <p className={`font-body text-xs mt-1 text-right ${charCount > 480 ? "text-peach" : "text-white/30"}`}>
                  {charCount}/500
                </p>
              </div>

              {/* Botão — abre WhatsApp com mensagem pronta */}
              <button
                type="submit"
                className="w-full font-body font-semibold text-sm py-4 transition-colors duration-300 whitespace-nowrap cursor-pointer mt-2 tracking-wide hover:bg-gray-100 flex items-center justify-center gap-2"
                style={{
                  backgroundColor: "#ffffff",
                  color: "rgb(61, 35, 32)",
                  borderRadius: "40px",
                }}
              >
                <i className="ri-whatsapp-line text-base" />
                Enviar pelo WhatsApp
              </button>

              <p className="font-body text-xs text-white/30 text-center">
                Suas informações são tratadas com total confidencialidade.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
